import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCopy, FiEdit, FiBook } from 'react-icons/fi';
import { adminAPI, API_ORIGIN } from '../../../services/api';
import ttsService from '../../../utils/textToSpeech';
import { useSidebar } from '../../../contexts/SidebarContext';
import { getStudentLessonShareUrl, getAdminLessonViewPath } from '../../../utils/lessonUrls';
import './AdminLessonView.css';

const AdminLessonView = () => {
    const { lessonId: lessonRef } = useParams();
    const navigate = useNavigate();
    const location = window.location;
    const getLessonRequestParams = () => {
        const searchParams = new URLSearchParams(location.search);
        const levelIdParam = searchParams.get('levelId');
        return levelIdParam ? { levelId: levelIdParam } : {};
    };
    const [lesson, setLesson] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [currentBlockIndex, setCurrentBlockIndex] = React.useState(0);
    const [copySuccess, setCopySuccess] = React.useState(false);
    const [showSentenceTranslations, setShowSentenceTranslations] = React.useState({});
    const [hoveredSentence, setHoveredSentence] = React.useState(null);
    const [hoveredPart, setHoveredPart] = React.useState(null);
    const [playbackSpeed, setPlaybackSpeed] = React.useState(1);
    const [activeSection, setActiveSection] = React.useState('sentences');
    const [expandedSidebarLessons, setExpandedSidebarLessons] = React.useState({});
    const [selectedTextSentence, setSelectedTextSentence] = React.useState(null);
    const [showTextTranslation, setShowTextTranslation] = React.useState(null);
    const { sidebarOpen, setSidebarOpen } = useSidebar();
    
    // Sidebar data
    const [sidebarCategory, setSidebarCategory] = React.useState('grammar');
    const [sidebarAreas, setSidebarAreas] = React.useState([]);
    const [sidebarSelectedArea, setSidebarSelectedArea] = React.useState(null);
    const [sidebarLessons, setSidebarLessons] = React.useState([]);
    const activeSidebarLessonRef = React.useRef(null);
    const activeSidebarBlockRef = React.useRef(null);

    // Считываем параметр block из URL
    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const blockParam = searchParams.get('block');
        if (blockParam !== null) {
            setCurrentBlockIndex(parseInt(blockParam, 10));
        }
    }, [location.search]);
    
    // Автоматически раскрываем текущий урок в сайдбаре
    React.useEffect(() => {
        if (lesson?.id && lesson.hasBlocks && lesson.blocks && lesson.blocks.length > 0) {
            // Автоматически загружаем блоки для текущего урока в sidebar
            setExpandedSidebarLessons(prev => ({
                ...prev,
                [lesson.id]: lesson.blocks
            }));
        }
    }, [lesson]);

    React.useEffect(() => {
        loadLesson();
        loadSidebarData();
    }, [lessonRef, location.search]);

    React.useEffect(() => {
        const activeItem = activeSidebarBlockRef.current || activeSidebarLessonRef.current;
        if (!activeItem) return;

        const frameId = window.requestAnimationFrame(() => {
            activeItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [lessonRef, sidebarLessons, expandedSidebarLessons, currentBlockIndex, lesson]);
    
    const loadSidebarData = async () => {
        try {
            console.log('Loading sidebar data for lesson (admin):', lessonRef);
            // Для админа берём данные урока и области/уровни через админские API
            const lessonRes = await adminAPI.getLessonBlocks(lessonRef, getLessonRequestParams());
            console.log('Lesson response (admin):', lessonRes.data);
            const currentLesson = lessonRes.data;
            const category = currentLesson.category || 'grammar';
            setSidebarCategory(category);
            console.log('Category:', category);
            
            const areasRes = await adminAPI.getAreas({ category });
            console.log('Areas response (admin):', areasRes.data);
            const areas = areasRes.data.areas || [];
            setSidebarAreas(areas);
            console.log('Areas:', areas);
            
            if (currentLesson.level_id && areas.length > 0) {
                const levelsPromises = areas.map(area => 
                    adminAPI.getLevels(area.id).then(res => ({
                        areaId: area.id,
                        levels: res.data.levels || []
                    }))
                );
                const areasWithLevels = await Promise.all(levelsPromises);
                console.log('Areas with levels (admin):', areasWithLevels);
                
                let foundArea = null;
                for (const areaData of areasWithLevels) {
                    if (areaData.levels.some(level => level.id === currentLesson.level_id)) {
                        foundArea = areas.find(a => a.id === areaData.areaId);
                        break;
                    }
                }
                
                console.log('Found area (admin):', foundArea);
                
                if (foundArea) {
                    setSidebarSelectedArea(foundArea.id);
                    const levelsRes = await adminAPI.getLevels(foundArea.id);
                    const levels = levelsRes.data.levels || [];
                    console.log('Levels for area (admin):', levels);
                    const lessonsPromises = levels.map((level) =>
                        adminAPI
                            .getLessonsBlocks({ levelId: level.id, category })
                            .then((res) => (Array.isArray(res.data) ? res.data : []))
                    );
                    const allLessonsArrays = await Promise.all(lessonsPromises);
                    const allLessons = allLessonsArrays.flat().filter((l) => l.category === category);
                    console.log('All lessons (admin):', allLessons);
                    setSidebarLessons(allLessons);
                }
            }
        } catch (error) {
            console.error('Ошибка загрузки данных бокового меню:', error);
        }
    };

    const loadLesson = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getLessonBlocks(lessonRef, getLessonRequestParams());
            const raw = response.data || {};
            const blocks = raw.blocks || [];
            const lessonData = {
                ...raw,
                blocks,
                hasBlocks: blocks.length > 0,
            };
            console.log('Loaded lesson data (admin blocks):', lessonData);
            console.log('Has blocks:', lessonData.hasBlocks);
            console.log('Blocks:', lessonData.blocks);
            
            // Парсим semantic_parts для блоков с текстом
            if (lessonData.hasBlocks && lessonData.blocks) {
                lessonData.blocks.forEach(block => {
                    if (block.block_type === 'text' && block.text) {
                        if (typeof block.text.semantic_parts === 'string') {
                            try {
                                block.text.semantic_parts = JSON.parse(block.text.semantic_parts);
                            } catch (e) {
                                console.error('Error parsing semantic_parts:', e);
                                block.text.semantic_parts = [];
                            }
                        }
                    }
                });
            }
            
            setLesson(lessonData);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки урока:', error);
            setLoading(false);
        }
    };
    
    const toggleSidebarLessonExpand = async (lessonIdToToggle) => {
        const isExpanded = expandedSidebarLessons[lessonIdToToggle];
        if (!isExpanded) {
            try {
                const response = await adminAPI.getLessonBlocks(lessonIdToToggle);
                const raw = response.data || {};
                const lessonData = { ...raw, blocks: raw.blocks || [], hasBlocks: (raw.blocks || []).length > 0 };
                setExpandedSidebarLessons(prev => ({
                    ...prev,
                    [lessonIdToToggle]: lessonData.blocks || []
                }));
            } catch (error) {
                console.error('Ошибка загрузки блоков урока:', error);
            }
        } else {
            setExpandedSidebarLessons(prev => ({
                ...prev,
                [lessonIdToToggle]: false
            }));
        }
    };

    const copyBlockLink = () => {
        const url = lesson ? getStudentLessonShareUrl(lesson, currentBlockIndex) : '';
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    const copyLessonLink = () => {
        const url = lesson ? getStudentLessonShareUrl(lesson) : '';
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };
    
    const playSentence = (text, voiceId = 'en-US-JennyNeural', audioUrl = null) => {
        if (audioUrl) {
            const audio = new Audio(`${API_ORIGIN}${audioUrl}`);
            audio.playbackRate = playbackSpeed;
            audio.play().catch(error => {
                console.error('Ошибка воспроизведения:', error);
            });
        } else {
            const cleanText = text.replace(/\s*\|\s*/g, ' ').trim();
            ttsService.speak(cleanText, {
                rate: playbackSpeed,
                lang: 'en-US',
                voice: voiceId
            }).catch(error => {
                console.error('Ошибка TTS:', error);
            });
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="loading">Загрузка урока...</div>
            </div>
        );
    }

    if (!lesson) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="error">Урок не найден</div>
            </div>
        );
    }

    const hasBlocks = lesson.hasBlocks && lesson.blocks && lesson.blocks.length > 0;
    const currentBlock = hasBlocks ? lesson.blocks[currentBlockIndex] : null;

    return (
        <div className="admin-lesson-view-container" style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            {/* Overlay для мобильных */}
            {sidebarOpen && (
                <div 
                    className="lesson-sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Боковое меню - точно как у студента */}
            <aside className={`lesson-sidebar ${sidebarOpen ? 'open' : ''}`} style={{
                width: '280px',
                background: '#f8f9fa',
                borderRight: '1px solid #dee2e6',
                overflowY: 'auto',
                height: '100%'
            }}>
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #dee2e6',
                    background: 'white'
                }}>
                    <h3 style={{ 
                        margin: '0 0 15px 0', 
                        fontSize: '16px',
                        color: '#333',
                        fontWeight: '600'
                    }}>
                        {sidebarCategory === 'grammar' ? 'Грамматика' : 'Лексика'}
                    </h3>
                    {sidebarAreas.map(area => (
                        <div
                            key={area.id}
                            onClick={() => {
                                if (sidebarSelectedArea === area.id) {
                                    setSidebarSelectedArea(null);
                                    setSidebarLessons([]);
                                } else {
                                    setSidebarSelectedArea(area.id);
                                    adminAPI.getLevels(area.id).then((res) => {
                                        const levels = res.data.levels || [];
                                        Promise.all(
                                            levels.map((level) =>
                                                adminAPI
                                                    .getLessonsBlocks({ levelId: level.id, category: sidebarCategory })
                                                    .then((r) => (Array.isArray(r.data) ? r.data : []))
                                            )
                                        ).then((arrays) => {
                                            setSidebarLessons(
                                                arrays.flat().filter((l) => l.category === sidebarCategory)
                                            );
                                        });
                                    });
                                }
                            }}
                            style={{
                                padding: '12px 16px',
                                margin: '8px 0',
                                background: sidebarSelectedArea === area.id ? '#e7f3ff' : 'white',
                                border: `2px solid ${sidebarSelectedArea === area.id ? '#4a90e2' : '#e0e0e0'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <FiBook style={{ fontSize: '20px', color: '#4a90e2', flexShrink: 0 }} />
                            <span style={{ 
                                fontSize: '14px', 
                                fontWeight: sidebarSelectedArea === area.id ? '600' : '500',
                                color: '#333'
                            }}>
                                {area.name || area.title}
                            </span>
                        </div>
                    ))}
                </div>
                
                {/* Список уроков */}
                {sidebarLessons.length > 0 && (
                    <div style={{ padding: '15px' }}>
                        <h4 style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '14px', 
                            color: '#666',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Уроки
                        </h4>
                        {sidebarLessons.map((sidebarLesson, index) => {
                            const isCurrentLesson = String(sidebarLesson.id) === String(lesson?.id);
                            const isExpanded = expandedSidebarLessons[sidebarLesson.id];
                            const hasLessonBlocks = Array.isArray(isExpanded) && isExpanded.length > 0;
                            const levelLabel = sidebarLesson.level_name || `Уровень ${sidebarLesson.level_id || ''}`.trim();
                            const previousLesson = sidebarLessons[index - 1];
                            const previousLevelLabel = previousLesson
                                ? previousLesson.level_name || `Уровень ${previousLesson.level_id || ''}`.trim()
                                : null;
                            const showLevelDivider = levelLabel && levelLabel !== previousLevelLabel;
                            
                            return (
                                <React.Fragment key={sidebarLesson.id}>
                                {showLevelDivider && (
                                    <div className="lesson-sidebar-level-divider">
                                        {levelLabel}
                                    </div>
                                )}
                                <div style={{ marginBottom: '8px' }}>
                                    <div
                                        ref={isCurrentLesson ? activeSidebarLessonRef : null}
                                        style={{
                                        padding: '10px 12px',
                                        background: isCurrentLesson ? '#e7f3ff' : 'white',
                                        border: `1px solid ${isCurrentLesson ? '#4a90e2' : '#e0e0e0'}`,
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: isCurrentLesson ? '600' : '500',
                                        color: isCurrentLesson ? '#4a90e2' : '#333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    >
                                        <span
                                            onClick={() => navigate(getAdminLessonViewPath(sidebarLesson))}
                                            style={{ flex: 1 }}
                                        >
                                            {sidebarLesson.title}
                                        </span>
                                        {isCurrentLesson && hasBlocks && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSidebarLessonExpand(sidebarLesson.id);
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    color: '#4a90e2',
                                                    padding: '0 5px'
                                                }}
                                            >
                                                {isExpanded ? '▲' : '▼'}
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Блоки урока */}
                                    {isCurrentLesson && isExpanded && Array.isArray(isExpanded) && isExpanded.length > 0 && (
                                        <div style={{ marginLeft: '15px', marginTop: '5px' }}>
                                            {isExpanded.map((block, blockIdx) => {
                                                const isActive = currentBlockIndex === blockIdx;
                                                return (
                                                    <div
                                                        key={blockIdx}
                                                        ref={isActive ? activeSidebarBlockRef : null}
                                                        onClick={() => {
                                                            setCurrentBlockIndex(blockIdx);
                                                            const url = new URL(window.location);
                                                            url.searchParams.set('block', blockIdx);
                                                            window.history.pushState({}, '', url);
                                                        }}
                                                        style={{
                                                            padding: '8px 10px',
                                                            margin: '4px 0',
                                                            background: isActive ? '#d0e7ff' : '#f8f9fa',
                                                            border: `1px solid ${isActive ? '#4a90e2' : '#e0e0e0'}`,
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            color: isActive ? '#4a90e2' : '#666',
                                                            fontWeight: isActive ? '600' : '400'
                                                        }}
                                                    >
                                                        {block.title}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
            </aside>

            {/* Основной контент */}
            <main style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
            }}>
                {/* Верхняя панель с кнопками */}
                <div style={{
                    padding: '20px 30px',
                    background: 'white',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <button 
                            onClick={() => navigate('/admin/view-lessons')}
                            style={{
                                padding: '8px 16px',
                                background: '#f0f0f0',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            ← Назад
                        </button>
                        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '600' }}>{lesson.title}</h1>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {copySuccess && (
                            <span style={{ color: '#52c41a', fontSize: '14px', fontWeight: '500' }}>
                                ✓ Скопировано!
                            </span>
                        )}
                        <button 
                            onClick={copyLessonLink}
                            style={{
                                padding: '8px 16px',
                                background: '#fff',
                                border: '1px solid #4a90e2',
                                color: '#4a90e2',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FiCopy />
                            Ссылка на урок
                        </button>
                        {hasBlocks && (
                            <button 
                                onClick={copyBlockLink}
                                style={{
                                    padding: '8px 16px',
                                    background: '#4a90e2',
                                    border: 'none',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FiCopy />
                                Ссылка на блок
                            </button>
                        )}
                        <button 
                            onClick={() => navigate(`/admin/lessons-blocks/edit/${lesson?.id}`)}
                            style={{
                                padding: '8px 16px',
                                background: '#52c41a',
                                border: 'none',
                                color: 'white',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FiEdit />
                            Редактировать
                        </button>
                    </div>
                </div>

                {/* Контент урока - прокручиваемая область */}
                <div style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    padding: '30px',
                    background: '#f8f9fa'
                }}>
                    {hasBlocks ? (
                        <>
                            {/* Отображение текущего блока */}
                            {currentBlock && (
                                <div style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '30px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}>
                                    <h2 style={{ 
                                        margin: '0 0 20px 0', 
                                        fontSize: '20px',
                                        color: '#333'
                                    }}>
                                        {currentBlock.title}
                                    </h2>
                                    {currentBlock.description ? (
                                        <p style={{
                                            margin: '-12px 0 20px 0',
                                            fontSize: '14px',
                                            color: '#666',
                                            lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {currentBlock.description}
                                        </p>
                                    ) : null}

                                    {/* Теория */}
                                    {currentBlock.block_type === 'theory' && (
                                        <div 
                                            className="theory-content"
                                            dangerouslySetInnerHTML={{ __html: currentBlock.content }}
                                            style={{
                                                fontSize: '15px',
                                                lineHeight: '1.8',
                                                color: '#444'
                                            }}
                                        />
                                    )}

                                    {/* Предложения */}
                                    {currentBlock.block_type === 'sentences' && currentBlock.sentences && currentBlock.sentences.length > 0 && (
                                        <div>
                                            {currentBlock.sentences.map((sentence, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        background: '#f8f9fa',
                                                        padding: '20px',
                                                        borderRadius: '8px',
                                                        marginBottom: '15px',
                                                        border: '1px solid #e0e0e0'
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        gap: '15px'
                                                    }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: '500',
                                                                color: '#2c3e50',
                                                                marginBottom: '8px',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => playSentence(sentence.sentence_en, sentence.voice_id, sentence.audio_url)}
                                                            >
                                                                {sentence.sentence_en}
                                                            </div>
                                                            {showSentenceTranslations[sentence.id] && (
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    color: '#666',
                                                                    fontStyle: 'italic'
                                                                }}>
                                                                    {sentence.sentence_ru}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setShowSentenceTranslations(prev => ({
                                                                ...prev,
                                                                [sentence.id]: !prev[sentence.id]
                                                            }))}
                                                            style={{
                                                                padding: '6px 12px',
                                                                background: '#4a90e2',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {showSentenceTranslations[sentence.id] ? 'Скрыть' : 'Показать'} перевод
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {!!Number(currentBlock.combined_speech_task) && (
                                                <div
                                                    style={{
                                                        marginTop: '12px',
                                                        padding: '12px 14px',
                                                        background: '#e6f7ff',
                                                        border: '1px solid #91d5ff',
                                                        borderRadius: '8px',
                                                        fontSize: '13px',
                                                        color: '#333',
                                                    }}
                                                >
                                                    Итоговое задание включено: у ученика остаются записи по строкам и
                                                    дополнительно внизу — одна запись всех предложений подряд.
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Текст */}
                                    {currentBlock.block_type === 'text' && currentBlock.text && (
                                        <div>
                                            {currentBlock.text.image_url && (
                                                <img 
                                                    src={currentBlock.text.image_url} 
                                                    alt={currentBlock.text.title}
                                                    style={{
                                                        maxWidth: '100%',
                                                        borderRadius: '8px',
                                                        marginBottom: '20px'
                                                    }}
                                                />
                                            )}
                                            <div style={{ fontSize: '15px', lineHeight: '1.8', color: '#444' }}>
                                                {currentBlock.text.semantic_parts && Array.isArray(currentBlock.text.semantic_parts) && currentBlock.text.semantic_parts.length > 0 ? (
                                                    currentBlock.text.semantic_parts.map((part, idx) => (
                                                        <span
                                                            key={idx}
                                                            style={{
                                                                cursor: 'pointer',
                                                                borderBottom: hoveredPart === idx ? '2px solid #4a90e2' : 'none'
                                                            }}
                                                            onMouseEnter={() => setHoveredPart(idx)}
                                                            onMouseLeave={() => setHoveredPart(null)}
                                                            onClick={() => playSentence(part.en, currentBlock.text.voice_id, part.audio_url)}
                                                        >
                                                            {part.en}{' '}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <p>{currentBlock.text.full_text_en || 'Текст не загружен'}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Видео */}
                                    {currentBlock.block_type === 'video' && currentBlock.video && (
                                        <div>
                                            {currentBlock.video.video_type === 'upload' && currentBlock.video.video_url && (
                                                <video
                                                    src={currentBlock.video.video_url.startsWith('http')
                                                        ? currentBlock.video.video_url
                                                        : `${API_ORIGIN}${currentBlock.video.video_url}`}
                                                    controls
                                                    style={{ width: '100%', maxHeight: '480px', borderRadius: '8px' }}
                                                />
                                            )}
                                            {currentBlock.video.video_type !== 'upload' && currentBlock.video.video_url && (
                                                <iframe
                                                    width="100%"
                                                    height="400"
                                                    src={currentBlock.video.video_url}
                                                    title={currentBlock.video.title}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    style={{ borderRadius: '8px' }}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* Упражнения */}
                                    {currentBlock.block_type === 'exercises' && currentBlock.exercises && currentBlock.exercises.length > 0 && (
                                        <div>
                                            {currentBlock.exercises.map((exercise, exIdx) => {
                                                const questions = exercise.questions || [];
                                                return (
                                                    <div
                                                        key={exIdx}
                                                        style={{
                                                            background: '#f8f9fa',
                                                            padding: '20px',
                                                            borderRadius: '12px',
                                                            marginBottom: '20px',
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    >
                                                        <div style={{ 
                                                            fontSize: '18px', 
                                                            fontWeight: '600', 
                                                            color: '#333', 
                                                            marginBottom: '8px' 
                                                        }}>
                                                            {exercise.title}
                                                        </div>
                                                        <div style={{ 
                                                            fontSize: '13px', 
                                                            color: '#999', 
                                                            marginBottom: '15px' 
                                                        }}>
                                                            Тип: {exercise.exercise_type === 'multiple_choice' ? 'Выбор варианта' :
                                                                   exercise.exercise_type === 'true_false' ? 'Правда/Ложь' :
                                                                   exercise.exercise_type === 'fill_gaps' ? 'Заполнить пропуски' :
                                                                   exercise.exercise_type === 'match' ? 'Сопоставление' :
                                                                   exercise.exercise_type === 'arrange' ? 'Расставить слова' :
                                                                   exercise.exercise_type === 'text_answer' ? 'Текстовый ответ' :
                                                                   exercise.exercise_type === 'phonetic_dictation' ? 'Фонетический диктант' : exercise.exercise_type}
                                                        </div>
                                                        
                                                        {/* Вопросы */}
                                                        {questions.length > 0 ? (
                                                            <div style={{ marginTop: '15px' }}>
                                                                {questions.map((question, qIdx) => {
                                                                    let questionData = {};
                                                                    let correctAnswer = question.correct_answer;
                                                                    
                                                                    try {
                                                                        questionData = typeof question.question_data === 'string' 
                                                                            ? JSON.parse(question.question_data) 
                                                                            : question.question_data;
                                                                    } catch (e) {
                                                                        console.error('Error parsing question_data:', e);
                                                                    }
                                                                    
                                                                    try {
                                                                        if (typeof correctAnswer === 'string' && 
                                                                            (correctAnswer.startsWith('{') || correctAnswer.startsWith('['))) {
                                                                            correctAnswer = JSON.parse(correctAnswer);
                                                                        }
                                                                    } catch (e) {
                                                                        // Оставляем как есть
                                                                    }
                                                                    
                                                                    return (
                                                                        <div
                                                                            key={qIdx}
                                                                            style={{
                                                                                background: 'white',
                                                                                padding: '15px',
                                                                                borderRadius: '8px',
                                                                                marginBottom: '12px',
                                                                                border: '1px solid #e0e0e0'
                                                                            }}
                                                                        >
                                                                            <div style={{ 
                                                                                fontSize: '15px', 
                                                                                fontWeight: '500', 
                                                                                color: '#333',
                                                                                marginBottom: '10px'
                                                                            }}>
                                                                                {qIdx + 1}. {question.question_text}
                                                                            </div>
                                                                            
                                                                            {/* Варианты ответов для multiple_choice */}
                                                                            {exercise.exercise_type === 'multiple_choice' && questionData.options && (
                                                                                <div style={{ paddingLeft: '20px' }}>
                                                                                    {questionData.options.map((option, optIdx) => (
                                                                                        <div 
                                                                                            key={optIdx}
                                                                                            style={{
                                                                                                padding: '8px',
                                                                                                margin: '4px 0',
                                                                                                background: option === correctAnswer ? '#e8f5e9' : '#f5f5f5',
                                                                                                borderLeft: option === correctAnswer ? '3px solid #4caf50' : '3px solid transparent',
                                                                                                borderRadius: '4px',
                                                                                                fontSize: '14px'
                                                                                            }}
                                                                                        >
                                                                                            {option} {option === correctAnswer && '✓'}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {/* True/False */}
                                                                            {exercise.exercise_type === 'true_false' && (
                                                                                <div style={{ 
                                                                                    fontSize: '14px', 
                                                                                    color: '#4caf50',
                                                                                    fontWeight: '600',
                                                                                    paddingLeft: '20px'
                                                                                }}>
                                                                                    Правильный ответ: {correctAnswer === 'true' || correctAnswer === true ? 'Правда' : 'Ложь'}
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {/* Fill gaps */}
                                                                            {exercise.exercise_type === 'fill_gaps' && (
                                                                                <div style={{ paddingLeft: '20px' }}>
                                                                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                                                                                        Предложение: {questionData.sentence}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '14px', color: '#4caf50', fontWeight: '600' }}>
                                                                                        Правильные ответы: {Array.isArray(correctAnswer) ? correctAnswer.join(', ') : correctAnswer}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {/* Match */}
                                                                            {exercise.exercise_type === 'match' && questionData.pairs && (
                                                                                <div style={{ paddingLeft: '20px' }}>
                                                                                    {questionData.pairs.map((pair, pIdx) => (
                                                                                        <div key={pIdx} style={{ 
                                                                                            fontSize: '14px', 
                                                                                            margin: '5px 0',
                                                                                            padding: '5px 0',
                                                                                            borderBottom: '1px solid #eee'
                                                                                        }}>
                                                                                            {pair.left} ⟷ {pair.right}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {/* Arrange */}
                                                                            {exercise.exercise_type === 'arrange' && (
                                                                                <div style={{ paddingLeft: '20px' }}>
                                                                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                                                                                        Слова для составления: {questionData.words ? questionData.words.join(', ') : ''}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '14px', color: '#4caf50', fontWeight: '600' }}>
                                                                                        Правильный порядок: {correctAnswer}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {exercise.exercise_type === 'phonetic_dictation' && (
                                                                                <div style={{ paddingLeft: '20px' }}>
                                                                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '6px' }}>
                                                                                        Источник: {(questionData.audioType || (questionData.audioUrl ? 'custom' : 'auto')) === 'custom' ? 'загруженный файл' : 'авто-озвучка (TTS)'}
                                                                                    </div>
                                                                                    {questionData.audioUrl && (
                                                                                        <audio controls src={`${API_ORIGIN}${questionData.audioUrl}`} style={{ width: '100%', maxWidth: '400px', marginBottom: '8px' }} />
                                                                                    )}
                                                                                    <div style={{ fontSize: '14px', color: '#4caf50', fontWeight: '600' }}>
                                                                                        Эталон (EN): {typeof correctAnswer === 'string' ? correctAnswer : JSON.stringify(correctAnswer)}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div style={{ 
                                                                fontSize: '14px', 
                                                                color: '#999', 
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                padding: '20px'
                                                            }}>
                                                                Вопросы не добавлены
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {/* Диалоги */}
                                    {currentBlock.block_type === 'dialog' && currentBlock.messages && currentBlock.messages.length > 0 && (
                                        <div>
                                            {currentBlock.messages.map((message, msgIdx) => (
                                                <div
                                                    key={msgIdx}
                                                    style={{
                                                        background: msgIdx % 2 === 0 ? '#f0f7ff' : '#fff9f0',
                                                        padding: '15px 20px',
                                                        borderRadius: '12px',
                                                        marginBottom: '12px',
                                                        border: '1px solid #e0e0e0'
                                                    }}
                                                >
                                                    <div style={{ 
                                                        fontSize: '13px', 
                                                        fontWeight: '600', 
                                                        color: '#1890ff',
                                                        marginBottom: '8px' 
                                                    }}>
                                                        {message.speaker}
                                                    </div>
                                                    
                                                    <div style={{ 
                                                        fontSize: '16px', 
                                                        fontWeight: '500', 
                                                        color: '#333',
                                                        marginBottom: '8px',
                                                        lineHeight: '1.5'
                                                    }}>
                                                        {message.message_en}
                                                    </div>
                                                    
                                                    {message.message_ru && (
                                                        <div style={{ 
                                                            fontSize: '14px', 
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                            marginTop: '4px'
                                                        }}>
                                                            {message.message_ru}
                                                        </div>
                                                    )}
                                                    
                                                    <div style={{ 
                                                        marginTop: '12px',
                                                        display: 'flex',
                                                        gap: '8px',
                                                        alignItems: 'center'
                                                    }}>
                                                        <button
                                                            onClick={() => playSentence(message.message_en, message.voice_id, message.audio_url)}
                                                            style={{
                                                                background: '#1890ff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                padding: '6px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            ▶ Прослушать
                                                        </button>
                                                        
                                                        {message.audio_url && (
                                                            <span style={{ 
                                                                fontSize: '12px', 
                                                                color: '#52c41a',
                                                                fontWeight: '500'
                                                            }}>
                                                                ✓ Загруженное аудио
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Навигация между блоками */}
                            <div className="block-navigation" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px',
                                marginTop: '30px',
                                padding: '20px',
                                background: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}>
                                <span style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                                    Блок {currentBlockIndex + 1} из {lesson.blocks.length}
                                </span>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '10px'
                                }}>
                                    <button
                                        onClick={() => {
                                            const newIndex = Math.max(0, currentBlockIndex - 1);
                                            setCurrentBlockIndex(newIndex);
                                            const url = new URL(window.location);
                                            url.searchParams.set('block', newIndex);
                                            window.history.pushState({}, '', url);
                                        }}
                                        disabled={currentBlockIndex === 0}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            background: currentBlockIndex === 0 ? '#f0f0f0' : '#4a90e2',
                                            color: currentBlockIndex === 0 ? '#999' : 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: currentBlockIndex === 0 ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ← Назад
                                    </button>
                                    <button
                                        onClick={() => {
                                            const newIndex = Math.min(lesson.blocks.length - 1, currentBlockIndex + 1);
                                            setCurrentBlockIndex(newIndex);
                                            const url = new URL(window.location);
                                            url.searchParams.set('block', newIndex);
                                            window.history.pushState({}, '', url);
                                        }}
                                        disabled={currentBlockIndex === lesson.blocks.length - 1}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            background: currentBlockIndex === lesson.blocks.length - 1 ? '#f0f0f0' : '#4a90e2',
                                            color: currentBlockIndex === lesson.blocks.length - 1 ? '#999' : 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: currentBlockIndex === lesson.blocks.length - 1 ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Далее →
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '40px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
                                Урок использует старый формат (без блоков)
                            </p>
                            <button 
                                onClick={() => navigate(`/admin/lessons/edit/${lesson?.id}`)}
                                style={{
                                    padding: '10px 20px',
                                    background: '#4a90e2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FiEdit />
                                Редактировать в старом редакторе
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminLessonView;
